import { useState } from "react";
import { Copy, Loader2, Play, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { apiPost } from "@/lib/apiAdmin";
import { HUD } from "./theme";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const inputCls = `h-10 rounded-xl px-3 text-sm border ${HUD.input}`;

const Panel = ({ title, desc, children }) => (
  <div className={`${HUD.panel} p-5 space-y-4`}>
    <div>
      <h3 className="text-white font-['Outfit'] text-lg">{title}</h3>
      {desc && <p className="text-xs text-gray-400 mt-1">{desc}</p>}
    </div>
    {children}
  </div>
);

const BriefingRow = ({ kind, label, b, setB }) => {
  const [running, setRunning] = useState(false);
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await apiPost("/admin/jarvis/briefing/run", { kind });
      toast.success(r.done?.[0] || "Done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setRunning(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-cyan-400/10 p-3">
      <Switch checked={!!b[`${kind}_enabled`]} onCheckedChange={(v) => setB({ ...b, [`${kind}_enabled`]: v })} data-testid={`jarvis-brief-${kind}-enabled`} />
      <div className="flex-1 min-w-[150px]">
        <p className="text-sm text-white">{label}</p>
        <p className="text-xs text-gray-500">Phoenix time (MST)</p>
      </div>
      <input type="time" value={b[`${kind}_time`]} onChange={(e) => setB({ ...b, [`${kind}_time`]: e.target.value })} data-testid={`jarvis-brief-${kind}-time`} className={`${inputCls} w-32`} />
      <button onClick={runNow} disabled={running} data-testid={`jarvis-brief-${kind}-run`} className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1`}>
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run now
      </button>
    </div>
  );
};

export const BriefingSection = ({ f, setF }) => {
  const b = f.briefing;
  const setB = (briefing) => setF({ ...f, briefing });
  const toggleDay = (d) => setB({ ...b, days: b.days.includes(d) ? b.days.filter((x) => x !== d) : [...b.days, d] });
  return (
    <Panel title="Scheduled briefing calls" desc="JARVIS calls your priority-1 team number at these times. If Vapi isn't connected yet, the briefing is posted in chat instead (free).">
      <BriefingRow kind="morning" label="Morning briefing — today's plan" b={b} setB={setB} />
      <BriefingRow kind="evening" label="Evening wrap-up — done, unpaid, rolls over" b={b} setB={setB} />
      <div className="flex flex-wrap gap-2">
        {DAYS.map((d, i) => (
          <button key={d} onClick={() => toggleDay(i)} data-testid={`jarvis-brief-day-${i}`} className={`h-8 px-3 rounded-full text-xs border transition-colors ${b.days.includes(i) ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/15 text-gray-500"}`}>
            {d}
          </button>
        ))}
      </div>
    </Panel>
  );
};

export const MoneyTextSection = ({ f, setF }) => {
  const mt = { enabled: true, time: "21:00", ...(f.money_text || {}) };
  const [sending, setSending] = useState(false);
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await apiPost("/admin/money/daily-text/send");
      toast.success(`${r.sent.map((x) => `${x.to}: ${x.status}`).join(", ") || "No owner phone"} — ${r.text}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };
  return (
    <Panel title="Daily money text & savings goals" desc="Texts team members whose role is 'owner' how much was made and spent today, plus what to put away for your goals. Add or pause goals just by telling JARVIS.">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-cyan-400/10 p-3">
        <Switch checked={!!mt.enabled} onCheckedChange={(v) => setF({ ...f, money_text: { ...mt, enabled: v } })} data-testid="jarvis-money-text-enabled" />
        <p className="text-sm text-white flex-1 min-w-[150px]">End-of-day text to owners</p>
        <input type="time" value={mt.time} onChange={(e) => setF({ ...f, money_text: { ...mt, time: e.target.value } })} data-testid="jarvis-money-text-time" className={`${inputCls} w-32`} />
        <button onClick={sendNow} disabled={sending} data-testid="jarvis-money-text-send" className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1`}>
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Send now
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[["gentle", "Gentle mention only"], ["firm", "Firm daily reminder (calls + texts)"]].map(([k, l]) => (
          <button key={k} onClick={() => setF({ ...f, goal_strictness: k })} data-testid={`jarvis-goal-strict-${k}`}
            className={`h-9 px-4 rounded-full text-xs border transition-colors ${(f.goal_strictness || "gentle") === k ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/15 text-gray-500"}`}>{l}</button>
        ))}
      </div>
    </Panel>
  );
};

export const DepositSection = ({ f, setF }) => (
  <Panel title="Deposits & phone booking" desc="Phone bookings hold the slot and text the customer a secure deposit link (Square). The booking confirms automatically once paid and a confirmation text goes out. Unpaid holds are released after the hold window.">
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="space-y-1.5 block">
        <span className={HUD.label}>Deposit % of job total</span>
        <input type="number" min={0} max={100} value={f.deposit_pct} onChange={(e) => setF({ ...f, deposit_pct: e.target.value })} data-testid="jarvis-set-deposit-pct" className={`${inputCls} w-full`} />
      </label>
      <label className="space-y-1.5 block">
        <span className={HUD.label}>Hold slot for (hours) while unpaid</span>
        <input type="number" min={1} max={168} value={f.deposit_hold_hours} onChange={(e) => setF({ ...f, deposit_hold_hours: e.target.value })} data-testid="jarvis-set-hold-hours" className={`${inputCls} w-full`} />
      </label>
    </div>
    <p className="text-[11px] text-gray-500">Applies to website quotes, phone bookings, admin appointments and counter-offers.</p>
  </Panel>
);

const CalendarRow = ({ c, onChange, onRemove, i }) => {
  const [testing, setTesting] = useState(false);
  const test = async () => {
    setTesting(true);
    try {
      const r = await apiPost("/admin/jarvis/calendars/test", { url: c.url });
      toast.success(`Connected — ${r.events_next_30d} event(s) in the next 30 days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read calendar");
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="grid grid-cols-[40px_1fr] sm:grid-cols-[40px_1fr_2fr_auto_40px] gap-2 items-center" data-testid={`jarvis-cal-row-${i}`}>
      <input type="color" value={c.color} onChange={(e) => onChange({ ...c, color: e.target.value })} className="w-10 h-10 rounded-xl bg-transparent border border-cyan-400/15" />
      <input placeholder="Label (e.g. Gmail)" value={c.label} onChange={(e) => onChange({ ...c, label: e.target.value })} data-testid={`jarvis-cal-label-${i}`} className={inputCls} />
      <input placeholder="Secret iCal / ICS URL" value={c.url} onChange={(e) => onChange({ ...c, url: e.target.value })} data-testid={`jarvis-cal-url-${i}`} className={`${inputCls} col-span-2 sm:col-span-1`} />
      <button onClick={test} disabled={!c.url || testing} data-testid={`jarvis-cal-test-${i}`} className={`${HUD.ghost} h-10 px-3 text-xs`}>
        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Test"}
      </button>
      <button onClick={onRemove} data-testid={`jarvis-cal-remove-${i}`} className="h-10 rounded-xl text-gray-500 hover:text-red-300 hover:bg-red-400/10 flex items-center justify-center">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

export const CalendarsSection = ({ f, setF, s }) => {
  const cals = f.calendars;
  const set = (calendars) => setF({ ...f, calendars });
  return (
    <Panel title="Linked calendars" desc="Busy times from these calendars block those slots on the website (the public just sees them as booked). Your admin calendar and JARVIS see full details.">
      {cals.map((c, i) => (
        <CalendarRow key={c.id || i} i={i} c={c} onChange={(n) => set(cals.map((x, j) => (j === i ? n : x)))} onRemove={() => set(cals.filter((_, j) => j !== i))} />
      ))}
      <button onClick={() => set([...cals, { label: "", url: "", color: "#a78bfa" }])} data-testid="jarvis-cal-add" className={`${HUD.ghost} h-9 px-4 text-sm flex items-center gap-1`}>
        <Plus className="w-4 h-4" /> Link a calendar
      </button>
      <ul className="text-[11px] text-gray-500 space-y-1 list-disc pl-4">
        <li><b className="text-gray-300">Gmail / Google Calendar:</b> calendar.google.com → Settings → your calendar → “Integrate calendar” → copy “Secret address in iCal format”.</li>
        <li><b className="text-gray-300">Outlook:</b> Settings → Calendar → Shared calendars → Publish → copy the ICS link.</li>
        <li><b className="text-gray-300">Apple iCloud:</b> Calendar → share calendar → Public Calendar → copy link (webcal:// works).</li>
      </ul>
      <div className="space-y-1.5 pt-2 border-t border-cyan-400/10">
        <span className={HUD.label}>Your bookings feed (subscribe in any calendar)</span>
        <div className="flex gap-2">
          <code className={`flex-1 rounded-xl px-3 py-2 text-[11px] text-cyan-200 bg-black/50 border border-cyan-400/10 break-all ${HUD.mono}`} data-testid="jarvis-cal-feed">{s.calendar_feed_url}</code>
          <button onClick={() => { navigator.clipboard?.writeText(s.calendar_feed_url); toast.success("Copied"); }} className={`${HUD.ghost} w-10 h-10 flex items-center justify-center shrink-0`}>
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-gray-500">Google Calendar → Other calendars “+” → From URL → paste. Shows every booking with customer, phone, address and status. Keep this link private.</p>
      </div>
    </Panel>
  );
};
